import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { AES, enc } from "crypto-js";
import { toast } from "react-toastify";
import Modal from "react-modal";
import { ThreeDots } from 'react-loader-spinner';
import PropTypes from 'prop-types';
import Currencies from '../../../constants/currencies';
import { setHeaderText } from '../../../features/headerText/headerTextSlice';

const normalizeToString = (rawValue) => {
  if (rawValue === null || rawValue === undefined) {
    return '';
  }
  if (typeof rawValue === 'string') {
    return rawValue;
  }
  if (typeof rawValue === 'number') {
    return rawValue.toString();
  }
  if (typeof rawValue === 'boolean') {
    return rawValue ? 'true' : 'false';
  }
  return rawValue?.toString?.() || '';
};

// Environment variable for encryption key (fallback to default for backward compatibility)
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "encryptionKey";


// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>{this.state.error?.message || 'An unexpected error occurred'}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

// Skeleton Loader Component
const SkeletonLoader = () => (
  <div className='plan-edit-outer'>
    <div className='plan-edit-top'>
      <div className='top-left-edit'>
        <div style={{ height: '24px', width: '200px', backgroundColor: '#e0e0e0', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
      </div>
      <div className='top-right-edit'>
        <div style={{ height: '32px', width: '80px', backgroundColor: '#e0e0e0', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
      </div>
    </div>
    <div className='plan-edit-inner'>
      <div style={{ padding: '20px' }}>
        <div style={{ height: '40px', width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
        <div style={{ height: '40px', width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginBottom: '16px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
        <div style={{ height: '40px', width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
      </div>
    </div>
    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `}</style>
  </div>
);

const FieldErrorMessage = ({ message, style }) => (
  <span
    style={{
      color: '#d32f2f',
      fontSize: '12px',
      display: 'block',
      minHeight: '18px',
      visibility: message ? 'visible' : 'hidden',
      ...style,
    }}
  >
    {message || '\u00A0'}
  </span>
);

FieldErrorMessage.propTypes = {
  message: PropTypes.string,
  style: PropTypes.object,
};

const EditPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { encryptedPlanId } = useParams(); // Get the plan ID from URL
  const token = useSelector((state) => state.masterAuth.masterUser.token);

  const [plan, setPlan] = useState({
    name: '',
    description: '',
    currency: '',
    trial_period: '',
    monthlyPrice: '',
    yearlyprice: '',
    features: [],
    duration: '',
    status: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [currenciesList, setCurrenciesList] = useState([]);
  const [currencyCodeSearchTerm, setCurrencyCodeSearchTerm] = useState('');
  const currencyDropdownRef = useRef(null);
  const [currencyCodeDropdownOpen, setCurrencyCodeDropdownOpen] = useState(false);
  const currencyCodeRef = useRef(null);
  const [durationDropdownOpen, setDurationDropdownOpen] = useState(false);
  const durationDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const abortControllerRef = useRef(null);
  const [decryptionError, setDecryptionError] = useState(null);

  // Decrypt ID with error handling - useMemo to avoid re-decrypting
  const id = useMemo(() => {
    try {
      const decodedId = decodeURIComponent(encryptedPlanId.replace(/%2F/g, "/"));
      const decryptedBytes = AES.decrypt(decodedId, ENCRYPTION_KEY);
      const decryptedId = decryptedBytes.toString(enc.Utf8);
      if (!decryptedId || decryptedId.trim() === '') {
        throw new Error('Invalid decrypted ID');
      }
      return decryptedId;
    } catch (error) {
      console.error('Error decrypting plan ID:', error);
      return null;
    }
  }, [encryptedPlanId]);

  // Handle decryption error in useEffect
  useEffect(() => {
    if (!id && encryptedPlanId) {
      setDecryptionError('Invalid plan ID. Please check the URL.');
      toast.error('Invalid plan ID. Please check the URL.', { position: "bottom-right", autoClose: 3000 });
      setTimeout(() => navigate("/master-user/subscriptions"), 2000);
    }
  }, [id, encryptedPlanId, navigate]);

  // Fetch plan data when component mounts with abort controller
  useEffect(() => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const fetchPlan = async () => {
      try {
        setError(null);
        const response = await fetch(`/api/subscriptions/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Unauthorized. Please log in again.');
          } else if (response.status === 404) {
            throw new Error('Subscription plan not found.');
          } else if (response.status >= 500) {
            throw new Error('Server error. Please try again later.');
          } else {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `Failed to fetch plan (${response.status})`);
          }
        }

        const data = await response.json();
        setPlan({
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || '',
          trial_period: data.trial_period ?? '',
          monthlyPrice: data.monthlyPrice ?? '',
          yearlyprice: data.yearlyprice ?? '',
          features: data.features || [],
          duration: data.duration || '',
          status: data.status || ''
        });
        setLoading(false);
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
          return;
        }
        console.error('Error fetching plan:', error);
        setError(error.message || 'Failed to load subscription plan');
        setLoading(false);
        toast.error(error.message || 'Failed to load subscription plan', { position: "bottom-right", autoClose: 3000 });
      }
    };

    const sortedCurrencies = Currencies.sourceCurrencies[0].targetCurrencies.slice()
      .sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
    setCurrenciesList(sortedCurrencies);
    dispatch(setHeaderText("Edit Subscription"));
    fetchPlan();

    const handleOutsideClick = (event) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target)) {
        setCurrencyCodeDropdownOpen(false);
      }
      if (durationDropdownRef.current && !durationDropdownRef.current.contains(event.target)) {
        setDurationDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [id, token, dispatch]);

// Field-level validation function
  const validateField = useCallback((field, value) => {
    const errors = {};
    const stringValue = normalizeToString(value);
    
    switch (field) {
      case 'name':
        if (stringValue.trim() === '') {
          errors.name = 'Name is required';
        } else if (stringValue.trim().length < 2) {
          errors.name = 'Name must be at least 2 characters';
        }
        break;
      case 'description':
        if (stringValue.trim() === '') {
          errors.description = 'Description is required';
        }
        break;
      case 'currency':
        if (stringValue.trim() === '') {
          errors.currency = 'Currency is required';
        }
        break;
      case 'trial_period':
        if (stringValue.trim() === '') {
          errors.trial_period = 'Trial period is required';
        } else {
          const num = Number(stringValue);
          if (isNaN(num) || num < 0) {
            errors.trial_period = 'Trial period must be a non-negative number';
          }
        }
        break;
      case 'monthlyPrice':
        if (stringValue.trim() === '') {
          errors.monthlyPrice = 'Monthly price is required';
        } else {
          const num = Number(stringValue);
          if (isNaN(num) || num < 0) {
            errors.monthlyPrice = 'Monthly price must be a non-negative number';
          }
        }
        break;
      case 'yearlyprice':
        if (stringValue.trim() === '') {
          errors.yearlyprice = 'Yearly price is required';
        } else {
          const num = Number(stringValue);
          if (isNaN(num) || num < 0) {
            errors.yearlyprice = 'Yearly price must be a non-negative number';
          }
        }
        break;
      default:
        break;
    }
    
    return errors;
  }, []);

  const validateFeatures = useCallback((featuresList) => {
    if (!featuresList || featuresList.length === 0) {
      return 'At least one feature is required';
    }
    const hasEmpty = featuresList.some(feature => normalizeToString(feature).trim() === '');
    if (hasEmpty) {
      return 'All features must have a value';
    }
    return '';
  }, []);

  const currencySymbol = useMemo(() => {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: plan.currency || 'USD' })
        .formatToParts(1)
        .find(p => p.type === 'currency')?.value || '$';
    } catch {
      return '$';
    }
  }, [plan.currency]);

  // Memoized filtered currencies
  const filteredCurrency = useMemo(() => {
    return currenciesList.filter((currency) => {
      const currencyName = `${currency.currencyCode} (${currency.currencyName})`;
      return currencyName?.toLowerCase().includes(currencyCodeSearchTerm?.toLowerCase());
    });
  }, [currenciesList, currencyCodeSearchTerm]);

  const handleFieldBlur = useCallback((field, value) => {
    const validationResult = validateField(field, value);
    setFieldErrors(prev => {
      const updated = { ...prev };
      delete updated[field];
      if (validationResult[field]) {
        updated[field] = validationResult[field];
      }
      return updated;
    });
  }, [validateField]);

  const applyFeatureValidation = useCallback((featuresList) => {
    const message = validateFeatures(featuresList);
    setFieldErrors(prev => {
      const updated = { ...prev };
      if (message) {
        updated.features = message;
      } else {
        delete updated.features;
      }
      return updated;
    });
  }, [validateFeatures]);

  // Handlers with useCallback
  const handleFieldChange = useCallback((field) => (e) => {
    const value = e.target.value;
    setPlan(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    
    // Validate on blur (optional - can be added to input onBlur)
  }, [fieldErrors]);

  const handleFeatureChange = useCallback((e, index) => {
    const updatedFeatures = [...plan.features];
    updatedFeatures[index] = e.target.value;
    setPlan({ ...plan, features: updatedFeatures });
    applyFeatureValidation(updatedFeatures);
  }, [plan, applyFeatureValidation]);

  const handleAddFeature = useCallback((e) => {
    e.preventDefault();
    setPlan(prev => {
      const updated = { ...prev, features: [...prev.features, ''] };
      applyFeatureValidation(updated.features);
      return updated;
    });
  }, [applyFeatureValidation]);

  const handleDeleteFeature = useCallback((index) => {
    setPlan(prev => {
      const updatedFeatures = prev.features.filter((_, i) => i !== index);
      const next = { ...prev, features: updatedFeatures };
      applyFeatureValidation(next.features);
      return next;
    });
  }, [applyFeatureValidation]);

  const handleToggleCurrencyDropdown = useCallback(() => {
    setCurrencyCodeDropdownOpen((prevOpen) => !prevOpen);
  }, []);

  const handleCurrencySearchInputChange = useCallback((e) => {
    setCurrencyCodeSearchTerm(e.target.value);
  }, []);

  const handleSave = useCallback(async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFieldErrors({});

    // Validate all fields
    const errors = {};
    const nameError = validateField('name', plan.name);
    const descError = validateField('description', plan.description);
    const currencyError = validateField('currency', plan.currency);
    const trialError = validateField('trial_period', plan.trial_period);
    const monthlyError = validateField('monthlyPrice', plan.monthlyPrice);
    const yearlyError = validateField('yearlyprice', plan.yearlyprice);

    Object.assign(errors, nameError, descError, currencyError, trialError, monthlyError, yearlyError);

    // Validate features
    const featureError = validateFeatures(plan.features);
    if (featureError) {
      errors.features = featureError;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error("Please fix the validation errors", { position: "bottom-right", autoClose: 3000 });
      setIsSaving(false);
      return;
    }

    try {
      const response = await fetch(`/api/master-user/subscriptions/edit/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(plan)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        } else if (response.status === 404) {
          throw new Error('Subscription plan not found.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to update plan (${response.status})`);
        }
      }

      toast.success('Plan updated successfully!', { position: "bottom-right", autoClose: 2000 });
      navigate("/master-user/subscriptions", { state: { fromEditOrAdd: true } });
    } catch (error) {
      console.error('Error saving plan:', error);
      const errorMessage = error.message || 'Failed to update plan. Please try again.';
      toast.error(errorMessage, { position: "bottom-right", autoClose: 3000 });
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [plan, id, token, navigate, validateField, validateFeatures]);

  const handleOpenDeleteModal = useCallback((e) => {
    e.preventDefault();
    setDeleteModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalOpen(false);
    setIsSubmitLoading(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setIsSubmitLoading(true);
    try {
      const response = await fetch(`/api/subscriptions/delete/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        } else if (response.status === 404) {
          throw new Error('Subscription plan not found.');
        } else if (response.status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Failed to delete plan (${response.status})`);
        }
      }

      toast.success('Plan deleted successfully!', { position: "bottom-right", autoClose: 2000 });
      setDeleteModalOpen(false);
      navigate("/master-user/subscriptions", { state: { fromEditOrAdd: true } });
    } catch (error) {
      console.error('Error deleting plan:', error);
      const errorMessage = error.message || 'Failed to delete plan. Please try again.';
      toast.error(errorMessage, { position: "bottom-right", autoClose: 3000 });
    } finally {
      setIsSubmitLoading(false);
    }
  }, [id, token, navigate]);

  // Show error if decryption failed
  if (decryptionError || !id) {
    return (
      <div className='plan-edit-outer'>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Invalid Plan ID</h2>
          <p style={{ color: '#d32f2f', marginBottom: '20px' }}>{decryptionError || 'Unable to decrypt plan ID'}</p>
          <button onClick={() => navigate("/master-user/subscriptions")}>Back to Subscriptions</button>
        </div>
      </div>
    );
  }

  // Show skeleton loader while loading
  if (loading) {
    return <SkeletonLoader />;
  }

  // Show error state if there's an error
  if (error && !loading) {
    return (
      <div className='plan-edit-outer'>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h2>Error Loading Plan</h2>
          <p style={{ color: '#d32f2f', marginBottom: '20px' }}>{error}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
          <button onClick={() => navigate("/master-user/subscriptions")} style={{ marginLeft: '10px' }}>
            Back to Subscriptions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='plan-edit-outer'>
      <div className='plan-edit-top'>
        <div className='top-left-edit'>
          {plan.name}
        </div>
        <div className='top-right-edit'>
          <button onClick={() => navigate("/master-user/subscriptions")}>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 11.5L11.5 1" stroke="#282828" />
                <path d="M1 1L11.5 11.5" stroke="#282828" />
              </svg>
            </span>
            Cancel
          </button>
        </div>
      </div>
      <div className='plan-edit-inner'>
        <form>
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-left'>
              <label>
                Name:
                <input
                  type="text"
                  value={plan.name}
                  onChange={handleFieldChange('name')}
                  className={fieldErrors.name ? 'error-input' : ''}
                  onBlur={(e) => handleFieldBlur('name', e.target.value)}
                />
                <FieldErrorMessage message={fieldErrors.name} />
              </label>
            </div>
            <div className='plan-edit-inner-top-right'>
              <label>
                Monthly Pricing ({currencySymbol}):
                <input
                  type="number"
                  value={plan.monthlyPrice}
                  onChange={handleFieldChange('monthlyPrice')}
                  min="0"
                  step="0.01"
                  className={fieldErrors.monthlyPrice ? 'error-input' : ''}
                  onBlur={(e) => handleFieldBlur('monthlyPrice', e.target.value)}
                />
                <FieldErrorMessage message={fieldErrors.monthlyPrice} />
              </label>
              <label>
                Yearly Pricing ({currencySymbol}):
                <input
                  type="number"
                  value={plan.yearlyprice}
                  onChange={handleFieldChange('yearlyprice')}
                  min="0"
                  step="0.01"
                  className={fieldErrors.yearlyprice ? 'error-input' : ''}
                  onBlur={(e) => handleFieldBlur('yearlyprice', e.target.value)}
                />
                <FieldErrorMessage message={fieldErrors.yearlyprice} />
              </label>
            </div>
          </div>

          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-right'>
              <div className="form-field-client">
                <label htmlFor="currency" className="form-label">
                  Currency
                </label>
                <FieldErrorMessage message={fieldErrors.currency} style={{ marginBottom: '5px' }} />
                <div ref={currencyDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                  <span
                    id="currency"
                    onClick={handleToggleCurrencyDropdown}
                    className="custom-dropdown-user-new"
                    ref={currencyCodeRef}
                  >
                    {plan.currency ? `${plan.currency} (${currencySymbol})` : 'Select Currency'}
                  </span>
                  {currencyCodeDropdownOpen && (
                    <div className="custom-dropdown-new">
                      <input
                        type="text"
                        className="search-bar"
                        placeholder="Search"
                        value={currencyCodeSearchTerm}
                        onChange={handleCurrencySearchInputChange}
                      />
                      <ul className="country-list">
                        {filteredCurrency.map((c) => (
                          <li
                            key={c.currencyCode}
                            className={plan.currency === c.currencyCode ? 'selected-item' : ''}
                            onClick={() => {
                              setPlan({ ...plan, currency: c.currencyCode });
                              setCurrencyCodeDropdownOpen(false);
                              handleFieldBlur('currency', c.currencyCode);
                            }}
                          >
                            {`${c.currencyCode} (${c.currencyName})`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '20px',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Trial period (days)</label>
                    <input
                      min={0}
                      type="number"
                      value={plan.trial_period}
                      onChange={handleFieldChange('trial_period')}
                      onBlur={(e) => handleFieldBlur('trial_period', e.target.value)}
                      className={fieldErrors.trial_period ? 'error-input' : ''}
                      style={{ display: 'block', width: '100%', height: '40px', borderRadius: '5px', border: '0.5px solid #B4B4B4', padding: '7px 15px', fontSize: '17px', fontWeight: '300', color: '#282828', boxSizing: 'border-box' }}
                    />
                  </div>
                  <FieldErrorMessage message={fieldErrors.trial_period} />
                </div>

                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Duration</label>
                    <div ref={durationDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                      <span
                        onClick={() => setDurationDropdownOpen(o => !o)}
                        className="custom-dropdown-user-new"
                      >
                        {{ daily: 'Day', weekly: 'Week', monthly: 'Month', yearly: 'Year' }[plan.duration] || 'Select Duration'}
                      </span>
                      {durationDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <ul className="country-list">
                            {[{ value: 'daily', label: 'Day' }, { value: 'weekly', label: 'Week' }, { value: 'monthly', label: 'Month' }, { value: 'yearly', label: 'Year' }].map(opt => (
                              <li
                                key={opt.value}
                                className={plan.duration === opt.value ? 'selected-item' : ''}
                                onClick={() => { setPlan(p => ({ ...p, duration: opt.value })); setDurationDropdownOpen(false); }}
                              >
                                {opt.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <FieldErrorMessage message={fieldErrors.duration} />
                </div>

                <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <div className="form-field-client">
                    <label className="form-label">Status</label>
                    <div ref={statusDropdownRef} className="select-modal-mainM custom-dropdown-user-new-outer">
                      <span
                        onClick={() => setStatusDropdownOpen(o => !o)}
                        className="custom-dropdown-user-new"
                      >
                        {plan.status ? plan.status.charAt(0).toUpperCase() + plan.status.slice(1) : 'Select Status'}
                      </span>
                      {statusDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <ul className="country-list">
                            {[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }].map(opt => (
                              <li
                                key={opt.value}
                                className={plan.status === opt.value ? 'selected-item' : ''}
                                onClick={() => { setPlan(p => ({ ...p, status: opt.value })); setStatusDropdownOpen(false); }}
                              >
                                {opt.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  <FieldErrorMessage message={null} />
                </div>

              </div>
            </div>
          </div>

          {/* Row 3: Description */}
          <div className='plan-edit-inner-top'>
            <div className='plan-edit-inner-top-right' style={{ width: '100%' }}>
              <div className="form-field-client" style={{ width: '100%' }}>
                <label className="form-label">Description</label>
                <textarea
                  value={plan.description}
                  onChange={handleFieldChange('description')}
                  onBlur={(e) => handleFieldBlur('description', e.target.value)}
                  rows={4}
                  className={fieldErrors.description ? 'error-input' : ''}
                  style={{ width: '100%', resize: 'vertical', borderRadius: '5px', border: '0.5px solid #B4B4B4', padding: '10px 15px', fontSize: '17px', fontWeight: '300', color: '#282828', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                <FieldErrorMessage message={fieldErrors.description} />
              </div>
            </div>
          </div>

          <div className='feature-edit-subscription'>
            <div className='feature-edit-title'>Plan features</div>
            <FieldErrorMessage message={fieldErrors.features} style={{ marginBottom: '10px' }} />
            <ul>
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(e, index)}
                  />
                  <button
                    type="button"
                    onClick={() => handleDeleteFeature(index)}
                    style={{ marginLeft: '10px', color: 'red' }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
                      {/* SVG paths remain the same as in AddPlan */}
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <button className="add-feature" onClick={handleAddFeature}>
              <span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M6.75 0V13.5" stroke="#282828" />
                  <path d="M13.5 6.75L0 6.75" stroke="#282828" />
                </svg>
              </span>
              Add Item
            </button>
          </div>
        </form>
      </div>
      <div className="plan-edit-inner-bottom">
        <button onClick={handleOpenDeleteModal} className="edit-delete">
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
              <path d="M16.9577 19.6445L17.4363 4.37763H19.5772V3.85209H14.0109V2.45942C14.0109 1.69739 13.4316 1.09302 12.7011 1.09302H7.966C7.23557 1.09302 6.65628 1.69739 6.65628 2.45942V3.85209H1.08997V4.37763H3.23085L3.70941 19.6445C3.73459 20.354 4.2887 20.9058 4.96875 20.9058H15.6732C16.3784 20.9058 16.9325 20.354 16.9577 19.6445ZM7.16001 2.45942C7.16001 1.98643 7.51263 1.61856 7.966 1.61856H12.7011C13.1545 1.61856 13.5071 1.98643 13.5071 2.45942V3.85209H7.16001V2.45942ZM4.21314 19.6182L3.73459 4.37763H16.9325L16.454 19.6182C16.4288 20.0387 16.1014 20.3803 15.6984 20.3803H4.96875C4.56576 20.3803 4.23833 20.0387 4.21314 19.6182Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M15.6736 21H4.96874C4.23877 21 3.64643 20.4064 3.61944 19.6486L3.14359 4.4723H1V3.75907H6.56631V2.46024C6.56631 1.64144 7.18114 1 7.96598 1H12.7011C13.486 1 14.1008 1.64144 14.1008 2.46024V3.75907H19.6671V4.4723H17.5231L17.0504 19.5515H17.0508L17.0472 19.6486C17.0202 20.4064 16.4166 21 15.6732 21H15.6736ZM1.17991 4.28414H3.3181L3.79935 19.6416C3.82273 20.2976 4.33637 20.8118 4.96874 20.8118H15.6736C16.3267 20.8118 16.8376 20.3149 16.8673 19.6538V19.6416L17.349 4.28414H19.4872V3.9463H13.9209V2.45978C13.9209 1.74608 13.3852 1.18722 12.7011 1.18722H7.96598C7.28189 1.18722 6.74622 1.74608 6.74622 2.45978V3.9463H1.17991V4.28414ZM4.96874 20.4745C4.52347 20.4745 4.15196 20.101 4.12363 19.6242V19.6214L3.64193 4.28367H17.0256L16.5439 19.6214C16.5151 20.101 16.1441 20.474 15.6984 20.474H4.96874V20.4745ZM4.30308 19.6139C4.32647 19.9912 4.61837 20.2868 4.96874 20.2868H15.6984C16.0492 20.2868 16.3415 19.9907 16.364 19.6125L16.8394 4.47137H3.82723L4.30263 19.6134L4.30308 19.6139ZM13.5971 3.9463H7.07005V2.45978C7.07005 1.93565 7.46359 1.52507 7.96598 1.52507H12.7011C13.2035 1.52507 13.5971 1.93565 13.5971 2.45978V3.9463ZM7.24995 3.7586H13.4172V2.45978C13.4172 2.04075 13.1028 1.71276 12.7011 1.71276H7.96598C7.56434 1.71276 7.24995 2.04075 7.24995 2.45978V3.7586Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M10.0811 8.37158H10.5848V16.2284H10.0811V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M10.6747 16.3223H9.99109V8.27783H10.6747V16.3223ZM10.171 16.1346H10.4948V8.46552H10.171V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M7.05884 8.37158H7.56258V16.2284H7.05884V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M7.65252 16.3223H6.96887V8.27783H7.65252V16.3223ZM7.14878 16.1346H7.47261V8.46552H7.14878V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M13.1046 8.37158H13.6084V16.2284H13.1046V8.37158Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
              <path d="M13.6983 16.3223H13.0146V8.27783H13.6983V16.3223ZM13.1946 16.1346H13.5184V8.46552H13.1946V16.1346Z" fill="#282828" stroke="#282828" stroke-width="0.5" />
            </svg>
          </span>
          Delete Plan
        </button>
        <button onClick={handleSave} className="edit-save" disabled={isSaving}>
          {isSaving ? (
            <>
              <ThreeDots
                height="20"
                width="20"
                radius="9"
                color="#fff"
                ariaLabel="saving"
                visible={true}
                wrapperStyle={{ display: 'inline-block', marginRight: '8px' }}
              />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
      <Modal
        className="delete-modal"
        isOpen={deleteModalOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Confirm Delete"
        style={{
          content: {
            height: "41%",
            width: "30%",
            position: "fixed",
            top: "36%",
            left: "50%",
            parent: document.querySelector(".admin-outer.time.tracker"),
          },
        }}
      >
        <div className="delete-modal-content">
          <h2>Confirm Delete</h2>
          <p>Are you sure you want to delete this plan?</p>
          {isSubmitLoading ? (
            <div className="outter-load-table">
              <ThreeDots
                height="38"
                width="40"
                radius="9"
                color="#6479f9"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
            </div>
          ) : (
            <div className="delete-modal-buttons">
              <button className="delete" onClick={handleConfirmDelete} disabled={isSubmitLoading}>
                Delete
              </button>
              <button onClick={handleCloseModal}>Close</button>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Wrap component with ErrorBoundary
const EditPlanWithErrorBoundary = () => (
  <ErrorBoundary>
    <EditPlan />
  </ErrorBoundary>
);

EditPlan.propTypes = {
  // Component doesn't receive props directly, but we can document expected behavior
};

export default EditPlanWithErrorBoundary;